// The item drawer (Item Info, SPEC §8.8): an item's fields as Sertica's Item page has them, and
// every PO line that ordered it (price, supplier, PO, date).
import { useQuery } from "@tanstack/react-query";
import { formatDate, formatWibLong } from "@po/shared";
import { api, type ItemDetail } from "../lib/api";
import { CriticalChip } from "./badges";
import type { DrawerRef } from "./Drawer";
import { Box, PurchaseHistory } from "./PositionDrawer";

export function ItemBody({ no, onOpen }: { no: string; onOpen: (r: DrawerRef) => void }) {
  const { data: i, isLoading, isError } = useQuery({
    queryKey: ["record", "item", no],
    queryFn: () => api<ItemDetail>(`/items/${encodeURIComponent(no)}`),
  });
  if (isLoading) return <div className="p-4 text-muted">Loading…</div>;
  if (isError || !i) return <div className="p-4 text-[13px] text-muted">Item {no} is not in the store. Search for it on the Item tab to read it from Sertica.</div>;
  const groups = i.groupNos.length ? `${i.groupNos.join(", ")}${i.groupNames ? ` · ${i.groupNames}` : ""}` : i.groupNames;
  return (
    <div>
      <div className="px-4 pt-3 pb-2 space-y-1.5">
        <div className="label">Item {i.no}</div>
        <h2 className="text-[18px] font-extrabold text-ink flex flex-wrap items-center gap-2">
          {i.name ?? "—"}
          {i.critical && <CriticalChip remark={i.criticalRemark} />}
          {i.inactive && <span className="text-[12px] font-semibold text-muted">Inactive</span>}
        </h2>
        <p className="text-[11.5px] text-muted">Stored from Sertica at {formatWibLong(new Date(i.syncedAt))}</p>
      </div>
      <section className="px-4 py-3 border-t border-line grid md:grid-cols-2 lg:grid-cols-3 gap-2" aria-label="Item details">
        <Box label="Item groups" wide>
          {groups}
        </Box>
        <Box label="Measuring unit">{i.measureUnitName ?? i.measureUnitNo}</Box>
        <Box label="Manufacturer">{i.manufacturer}</Box>
        <Box label="Classification">{i.classification}</Box>
        <Box label="Reference no.">{i.referenceNo}</Box>
        <Box label="Drawing no.">{i.drawingNo}</Box>
        <Box label="Drawing pos.">{i.drawingPosNo}</Box>
        <Box label="Type no.">{i.typeNo}</Box>
        <Box label="Created">{i.createDate && formatDate(i.createDate)}</Box>
        <Box label="Last updated">{i.updateDate && formatDate(i.updateDate)}</Box>
        <Box label="Text" wide area>
          {i.text}
        </Box>
        <Box label="Internal remark" wide area>
          {i.internalRemark}
        </Box>
      </section>
      <PurchaseHistory
        history={i.history}
        measureUnit={i.measureUnitName}
        onOpen={onOpen}
        intro={
          i.matchedBy === "item"
            ? `Every PO line ordering item ${i.no}, on any vessel. Cancelled POs are left out.`
            : `No stored PO line is linked to item ${i.no} yet (older lines are still being read for their item), so PO lines are matched by the same name. Cancelled POs are left out.`
        }
      />
    </div>
  );
}
